import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import AvaliacoesDetalhes from './AvaliacoesDetalhes';
import * as XLSX from 'xlsx';
import './ResultadosPremio.css';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const CATEGORIA_INFO = {
  temas_livres:     { titulo: 'Temas Livres',     grupoField: 'grupo_temas_livres',     emoji: '📋' },
  posteres:         { titulo: 'Pôsteres',         grupoField: 'grupo_posteres',         emoji: '🖼️' },
  temas_correlatos: { titulo: 'Temas Correlatos', grupoField: 'grupo_temas_correlatos', emoji: '🎤' },
};

const PREMIACAO_TOP_5 = [
  { icone: '🥇', etiqueta: 'Ouro',     classe: 'ouro',     pdf: '1o OURO' },
  { icone: '🥈', etiqueta: 'Prata',    classe: 'prata',    pdf: '2o PRATA' },
  { icone: '🥉', etiqueta: 'Bronze',   classe: 'bronze',   pdf: '3o BRONZE' },
  { icone: '🏅', etiqueta: 'Premiado', classe: 'premiado', pdf: '4o PREMIADO' },
  { icone: '🏅', etiqueta: 'Premiado', classe: 'premiado', pdf: '5o PREMIADO' },
];

function ResultadosPremio({ categoria = 'temas_livres' }) {
  const { user } = useAuth();
  const info = CATEGORIA_INFO[categoria] || CATEGORIA_INFO.temas_livres;
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalhesVisiveis, setDetalhesVisiveis] = useState({});
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [faltantes, setFaltantes] = useState({});
  const [loadingFaltantes, setLoadingFaltantes] = useState({});
  const [controlandoId, setControlandoId] = useState(null);
  const [avaliadores, setAvaliadores] = useState([]);
  const [showListaAvaliadores, setShowListaAvaliadores] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [detalhesAvaliacoes, setDetalhesAvaliacoes] = useState({});
  const versaoRef = useRef(null);

  const [abaAtiva, setAbaAtiva] = useState('media'); // 'media' | 'aparada' | 'zscore'

  const csrfHeaders = { headers: { 'X-CSRFToken': getCookie('csrftoken') } };
  const podeControlar = user?.is_superuser || user?.is_coordenador;

  // ── FUNÇÕES DE CÁLCULO ─────────────────────────────────────────

  // Média aritmética simples
  const calcMedia = (avaliacoes) => {
    if (!avaliacoes || avaliacoes.length === 0) return 0;
    const soma = avaliacoes.reduce((acc, a) => acc + parseFloat(a.nota_final_calculada), 0);
    return soma / avaliacoes.length;
  };

  // Média Aparada: remove maior e menor nota antes de calcular
  const calcMediaAparada = (avaliacoes) => {
    if (!avaliacoes || avaliacoes.length === 0) return 0;
    if (avaliacoes.length <= 2) return calcMedia(avaliacoes);
    const notas = avaliacoes.map(a => parseFloat(a.nota_final_calculada)).sort((a, b) => a - b);
    const aparadas = notas.slice(1, -1); // remove menor e maior
    return aparadas.reduce((acc, n) => acc + n, 0) / aparadas.length;
  };

  // Z-Score: normaliza notas por avaliador para eliminar viés de escala
  const calcZScore = (apresentacaoId, todasApresentacoes) => {
    // Agrupa notas por avaliador em todas as apresentações
    const notasPorAvaliador = {};
    const todasNotasValidas = [];
    todasApresentacoes.forEach(ap => {
      (ap.avaliacoes || []).forEach(av => {
        const nota = parseFloat(av.nota_final_calculada);
        if (!Number.isFinite(nota)) return;
        if (!notasPorAvaliador[av.avaliador_id]) notasPorAvaliador[av.avaliador_id] = [];
        notasPorAvaliador[av.avaliador_id].push(nota);
        todasNotasValidas.push(nota);
      });
    });

    // Distribuição geral usada somente quando o avaliador ainda não possui
    // histórico suficiente ou apresenta desvio-padrão igual a zero.
    const mediaGlobal = todasNotasValidas.length
      ? todasNotasValidas.reduce((a, b) => a + b, 0) / todasNotasValidas.length
      : 0;
    const varianciaGlobal = todasNotasValidas.length
      ? todasNotasValidas.reduce((acc, nota) => acc + Math.pow(nota - mediaGlobal, 2), 0) / todasNotasValidas.length
      : 0;
    const desvioGlobal = Math.sqrt(varianciaGlobal);

    // Calcula média e desvio padrão de cada avaliador
    const statsAvaliador = {};
    Object.entries(notasPorAvaliador).forEach(([avId, notas]) => {
      const media = notas.reduce((a, b) => a + b, 0) / notas.length;
      const variancia = notas.reduce((acc, n) => acc + Math.pow(n - media, 2), 0) / notas.length;
      const desvio = Math.sqrt(variancia);
      statsAvaliador[avId] = { media, desvio };
    });

    // Calcula Z-Score para cada avaliação desta apresentação
    const ap = todasApresentacoes.find(a => a.id === apresentacaoId);
    if (!ap || !ap.avaliacoes || ap.avaliacoes.length === 0) return 0;

    const zScores = ap.avaliacoes.map(av => {
      const nota = parseFloat(av.nota_final_calculada);
      if (!Number.isFinite(nota)) return null;
      const stats = statsAvaliador[av.avaliador_id];
      if (stats && stats.desvio > 0) return (nota - stats.media) / stats.desvio;
      if (desvioGlobal > 0) return (nota - mediaGlobal) / desvioGlobal;
      return 0;
    }).filter(z => z !== null);

    return zScores.length ? zScores.reduce((a, b) => a + b, 0) / zScores.length : 0;
  };

  // Ordena resultados conforme a aba ativa
  const resultadosOrdenados = React.useMemo(() => {
    if (!resultados.length) return [];
    const ordenar = (calcularNota) => [...resultados].sort((a, b) => {
      const aAvaliado = (a.avaliacoes || []).length > 0;
      const bAvaliado = (b.avaliacoes || []).length > 0;
      if (aAvaliado !== bAvaliado) return bAvaliado ? 1 : -1;
      if (!aAvaliado) return a.titulo.localeCompare(b.titulo, 'pt-BR');
      return calcularNota(b) - calcularNota(a);
    });
    if (abaAtiva === 'media') {
      return ordenar((ap) => calcMedia(ap.avaliacoes));
    }
    if (abaAtiva === 'aparada') {
      return ordenar((ap) => calcMediaAparada(ap.avaliacoes));
    }
    if (abaAtiva === 'zscore') {
      return ordenar((ap) => calcZScore(ap.id, resultados));
    }
    return resultados;
  }, [resultados, abaAtiva]); // eslint-disable-line

  const fetchResultados = useCallback(() => {
    setAtualizando(true);
    axios.get('/api/resultados-premio/', { params: { categoria } })
      .then(response => {
        const apresentacoes = response.data.resultados || [];
        versaoRef.current = response.data.versao;
        setAvaliadores(response.data.avaliadores || []);
        const ordenadas = apresentacoes.sort((a, b) => {
          const mediaA = a.avaliacoes.length > 0
            ? a.avaliacoes.reduce((acc, curr) => acc + parseFloat(curr.nota_final_calculada), 0) / a.avaliacoes.length : 0;
          const mediaB = b.avaliacoes.length > 0
            ? b.avaliacoes.reduce((acc, curr) => acc + parseFloat(curr.nota_final_calculada), 0) / b.avaliacoes.length : 0;
          return mediaB - mediaA;
        });
        setResultados(ordenadas);
        setUltimaAtualizacao(new Date());
        setLoading(false);
      })
      .catch(error => {
        console.error('Erro ao buscar resultados!', error);
        setLoading(false);
      })
      .finally(() => setAtualizando(false));
  }, [categoria]);

  useEffect(() => {
    fetchResultados();
    const interval = setInterval(() => {
      axios.get('/api/resultados-premio/', { params: { categoria, somente_versao: 1 } })
        .then(response => {
          if (versaoRef.current !== response.data.versao) fetchResultados();
          else setUltimaAtualizacao(new Date());
        })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchResultados, categoria]);

  const toggleDetalhes = async (id) => {
    if (detalhesVisiveis[id]) {
      setDetalhesVisiveis(prev => ({ ...prev, [id]: false }));
      return;
    }
    setDetalhesVisiveis(prev => ({ ...prev, [id]: true }));
    if (!detalhesAvaliacoes[id]) {
      try {
        const response = await axios.get(`/api/resultados-premio/${id}/detalhes/`);
        setDetalhesAvaliacoes(prev => ({ ...prev, [id]: response.data }));
      } catch {
        toast.error('Não foi possível carregar os detalhes das avaliações.');
      }
    }
  };

  // Abre ou fecha avaliação de uma apresentação
  const handleControle = async (ap, acao) => {
    setControlandoId(ap.id);
    try {
      const res = await axios.post(
        `/api/apresentacoes/${ap.id}/controle-avaliacao/`,
        { acao },
        csrfHeaders
      );
      toast.success(res.data.mensagem);

      // Se fechou, buscar lista de faltantes automaticamente
      if (acao === 'fechar') {
        await buscarFaltantes(ap.id);
      } else {
        // Se abriu, limpar faltantes anteriores
        setFaltantes(prev => { const n = { ...prev }; delete n[ap.id]; return n; });
      }
      fetchResultados();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar status da avaliação.');
    } finally {
      setControlandoId(null);
    }
  };

  // Busca lista de faltantes de uma apresentação
  const buscarFaltantes = async (apId) => {
    setLoadingFaltantes(prev => ({ ...prev, [apId]: true }));
    try {
      const res = await axios.get(`/api/apresentacoes/${apId}/faltantes/`);
      setFaltantes(prev => ({ ...prev, [apId]: res.data }));
    } catch {
      toast.error('Erro ao buscar lista de faltantes.');
    } finally {
      setLoadingFaltantes(prev => ({ ...prev, [apId]: false }));
    }
  };

  // Exportar faltantes para Excel
  const exportarFaltantesExcel = (ap) => {
    const dados = faltantes[ap.id];
    if (!dados) return;
    const wb = XLSX.utils.book_new();

    // Aba faltantes
    const linhasFaltantes = [
      [`AVALIADORES QUE NÃO AVALIARAM — ${ap.titulo}`],
      [`Gerado em: ${new Date().toLocaleString('pt-BR')}`],
      [],
      ['Nº', 'Usuário', 'Nome Completo', 'E-mail'],
      ...dados.faltantes.map(u => [u.numero_identificacao || '—', u.username, u.nome_completo, u.email || '—']),
    ];
    const wsFaltantes = XLSX.utils.aoa_to_sheet(linhasFaltantes);
    wsFaltantes['!cols'] = [{ wch: 6 }, { wch: 20 }, { wch: 28 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, wsFaltantes, 'Não Avaliaram');

    // Aba quem avaliou
    const linhasAvaliaram = [
      [`AVALIADORES QUE JÁ AVALIARAM — ${ap.titulo}`],
      [],
      ['Nº', 'Usuário', 'Nome Completo'],
      ...dados.ja_avaliaram.map(u => [u.numero_identificacao || '—', u.username, u.nome_completo]),
    ];
    const wsAvaliaram = XLSX.utils.aoa_to_sheet(linhasAvaliaram);
    XLSX.utils.book_append_sheet(wb, wsAvaliaram, 'Já Avaliaram');

    const nomeArquivo = `faltantes_${ap.titulo.substring(0, 30).replace(/\s/g, '_')}.xlsx`;
    XLSX.writeFile(wb, nomeArquivo);
    toast.success('Planilha exportada!');
  };

  const gerarPDF = async function() {
    setGerandoPdf(true);
    try {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      document.head.appendChild(script);
      await new Promise(function(resolve) { script.onload = resolve; });
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const dataHoje = new Date().toLocaleDateString('pt-BR');
      const pageW = 210; const margin = 15;
      const ordenarParaPdf = (calcularNota) => [...resultados].sort((a, b) => {
        const aAvaliado = (a.avaliacoes || []).length > 0;
        const bAvaliado = (b.avaliacoes || []).length > 0;
        if (aAvaliado !== bAvaliado) return bAvaliado ? 1 : -1;
        if (!aAvaliado) return a.titulo.localeCompare(b.titulo, 'pt-BR');
        return calcularNota(b) - calcularNota(a);
      });

      const metodos = {
        media: {
          key: 'media',
          label: 'Classificacao por Media Aritmetica',
          desc: 'Metodo oficial - soma de todas as notas dividida pelo numero de avaliadores',
          calcNota: (ap) => calcMedia(ap.avaliacoes),
          formatNota: (n) => n.toFixed(2),
          cor: [41, 128, 185],
          ordenado: ordenarParaPdf((ap) => calcMedia(ap.avaliacoes)),
        },
        aparada: {
          key: 'aparada',
          label: 'Classificacao por Media Aparada',
          desc: 'Remove a maior e a menor nota antes de calcular a media',
          calcNota: (ap) => calcMediaAparada(ap.avaliacoes),
          formatNota: (n) => n.toFixed(2),
          cor: [39, 174, 96],
          ordenado: ordenarParaPdf((ap) => calcMediaAparada(ap.avaliacoes)),
        },
        zscore: {
          key: 'zscore',
          label: 'Classificacao por Z-Score',
          desc: 'Normaliza por avaliador e usa a distribuicao geral quando nao ha historico suficiente',
          calcNota: (ap) => calcZScore(ap.id, resultados),
          formatNota: (n) => {
            const convertida = Math.min(100, Math.max(0, 50 + n * 10));
            return (n > 0 ? '+' : '') + n.toFixed(3) + 'o  (' + convertida.toFixed(1) + ' pts)';
          },
          cor: [155, 89, 182],
          ordenado: ordenarParaPdf((ap) => calcZScore(ap.id, resultados)),
        },
      };
      const metodoSelecionado = metodos[abaAtiva] || metodos.media;

      const desenharCabecalho = (doc, metodo, y0) => {
        // Header azul escuro
        doc.setFillColor(26, 46, 69);
        doc.rect(0, 0, pageW, 40, 'F');
        doc.setFillColor(201, 168, 76);
        doc.rect(0, 37, pageW, 3, 'F');
        doc.setTextColor(201, 168, 76);
        doc.setFontSize(15); doc.setFont('helvetica', 'bold');
        doc.text(info.titulo + ' - CBACV 2026', margin, 13);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10); doc.setFont('helvetica', 'normal');
        doc.text(metodo.label, margin, 22);
        doc.setFontSize(8);
        doc.text(metodo.desc, margin, 29);
        doc.text('Gerado em: ' + dataHoje, margin, 35);

        // Badge do método
        doc.setFillColor(metodo.cor[0], metodo.cor[1], metodo.cor[2]);
        doc.roundedRect(pageW - margin - 30, 8, 28, 10, 3, 3, 'F');
        doc.setTextColor(255,255,255); doc.setFontSize(7); doc.setFont('helvetica','bold');
        const labels = { media:'Media Aritm.', aparada:'Media Aparada', zscore:'Z-Score' };
        doc.text(labels[metodo.key], pageW - margin - 28, 14.5);
        return 50;
      };

      const desenharTabela = (doc, metodo, y) => {
        const comAval = metodo.ordenado.filter(r => r.avaliacoes.length > 0).length;
        doc.setTextColor(26, 46, 69); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
        doc.text('Total: ' + metodo.ordenado.length + ' trabalhos   |   Avaliados: ' + comAval, margin, y); y += 8;

        // Cabeçalho tabela
        doc.setFillColor(36, 59, 85);
        doc.rect(margin, y, pageW - margin * 2, 8, 'F');
        doc.setTextColor(201, 168, 76); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
        doc.text('Pos.', margin + 2, y + 5.5);
        doc.text('Titulo / Apresentador', margin + 24, y + 5.5);
        doc.text('Aval.', margin + 120, y + 5.5);
        doc.text('Nota', margin + 138, y + 5.5);
        y += 10;

        metodo.ordenado.forEach(function(ap, idx) {
          if (y > 268) {
            // Rodapé antes de nova página
            doc.setFillColor(26, 46, 69); doc.rect(0, 287, 210, 10, 'F');
            doc.setTextColor(189, 195, 199); doc.setFontSize(7); doc.setFont('helvetica', 'normal');
            doc.text(info.titulo + ' - CBACV 2026 | Evalon by NimVexx', margin, 293);
            doc.addPage();
            y = 15;
            // Mini header
            doc.setFillColor(26, 46, 69); doc.rect(0, 0, pageW, 10, 'F');
            doc.setTextColor(201, 168, 76); doc.setFontSize(8); doc.setFont('helvetica','bold');
            doc.text(info.titulo + ' — ' + metodo.label, margin, 7);
            y = 16;
          }

          const nota = ap.avaliacoes.length > 0 ? metodo.calcNota(ap) : null;
          const notaStr = nota !== null ? metodo.formatNota(nota) : '—';
          const premiado = nota !== null && idx < PREMIACAO_TOP_5.length;
          const bgTop5 = idx === 0 ? [255,248,220]
            : idx === 1 ? [245,245,245]
            : idx === 2 ? [255,240,230]
            : premiado ? [240,248,255]
            : [255,255,255];
          doc.setFillColor(bgTop5[0], bgTop5[1], bgTop5[2]);
          doc.rect(margin, y, pageW - margin * 2, 13, 'F');

          // Barra colorida lateral
          if (nota !== null) {
            const corBarra = premiado ? metodo.cor : [180,180,180];
            doc.setFillColor(corBarra[0], corBarra[1], corBarra[2]);
            doc.rect(margin, y, 3, 13, 'F');
          }

          // Posição
          const pos = premiado ? PREMIACAO_TOP_5[idx].pdf : (idx+1) + 'o';
          doc.setTextColor(26, 46, 69); doc.setFontSize(premiado && idx >= 3 ? 6.5 : 7.5); doc.setFont('helvetica','bold');
          doc.text(pos, margin + 4, y + 7);

          // Título
          const titulo = ap.titulo.length > 48 ? ap.titulo.substring(0, 46) + '...' : ap.titulo;
          doc.setFontSize(7.5); doc.text(titulo, margin + 24, y + 5);
          doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(100, 120, 150);
          doc.text(ap.apresentador_principal || '', margin + 24, y + 10);

          // Avaliações
          doc.setTextColor(26, 46, 69); doc.setFontSize(8);
          doc.text(String(ap.avaliacoes.length), margin + 123, y + 7);

          // Nota
          if (nota !== null) {
            doc.setTextColor(metodo.cor[0], metodo.cor[1], metodo.cor[2]);
          } else {
            doc.setTextColor(150,150,150);
          }
          doc.setFont('helvetica','bold'); doc.setFontSize(metodo.key === 'zscore' ? 7.5 : 10);
          doc.text(notaStr, margin + 138, y + 8);
          y += 15;
        });
        return y;
      };

      // Exportar somente o método que está selecionado na tela.
      const yInicio = desenharCabecalho(doc, metodoSelecionado, 0);
      desenharTabela(doc, metodoSelecionado, yInicio);

      // Rodapé em todas as páginas
      const totalPags = doc.getNumberOfPages();
      for (let i = 1; i <= totalPags; i++) {
        doc.setPage(i);
        doc.setFillColor(26, 46, 69); doc.rect(0, 287, 210, 10, 'F');
        doc.setTextColor(189, 195, 199); doc.setFontSize(7); doc.setFont('helvetica', 'normal');
        doc.text(info.titulo + ' - CBACV 2026 | Evalon by NimVexx · Mahfouz Tech', margin, 293);
        doc.text('Pagina ' + i + ' de ' + totalPags, 185, 293);
      }

      doc.save('CBACV2026_' + categoria + '_' + metodoSelecionado.key + '_' + dataHoje.replace(/\//g, '-') + '.pdf');
      toast.success('PDF gerado: ' + metodoSelecionado.label + '!');
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      toast.error('Erro ao gerar PDF.');
    }
    setGerandoPdf(false);
  };

  if (loading) return <div className="premio-container"><p>Carregando...</p></div>;

  return (
    <div className="premio-container">
      <div className="premio-header">
        <div className="premio-header-left">
          <div className="premio-icon">{info.emoji}</div>
          <div>
            <span className="premio-eyebrow">CBACV 2026 · Resultados oficiais</span>
            <h1 className="premio-titulo">{info.titulo}</h1>
            <p className="premio-subtitulo">Classificação final · cinco primeiros colocados premiados</p>
          </div>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', justifyContent:'flex-end'}}>
          <span style={{fontSize:'13px', opacity:0.8}}>
            {ultimaAtualizacao ? `Atualizado às ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : ''}
          </span>
          <button className="premio-btn-pdf" onClick={fetchResultados} disabled={atualizando}>
            {atualizando ? '⏳ Atualizando...' : '↻ Atualizar agora'}
          </button>
          <button className="premio-btn-pdf" onClick={gerarPDF} disabled={gerandoPdf || resultados.length === 0}>
            {gerandoPdf ? '⏳ Gerando...' : '📄 Exportar PDF'}
          </button>
        </div>
      </div>

      {/* Painel de avaliadores com números — apenas para quem pode controlar */}
      {podeControlar && avaliadores.length > 0 && (
        <div className="premio-avaliadores-painel">
          <div
            className="premio-avaliadores-header"
            onClick={() => setShowListaAvaliadores(!showListaAvaliadores)}
            style={{cursor:'pointer'}}
          >
            <strong>👥 Avaliadores — {info.titulo} — Números de Identificação</strong>
            <span style={{fontSize:'13px', color:'#6b7a95', marginLeft:'8px'}}>
              ({avaliadores.length} avaliador(es))
            </span>
            <span style={{marginLeft:'auto'}}>{showListaAvaliadores ? '▲' : '▼'}</span>
          </div>
          {showListaAvaliadores && (
            <div className="premio-avaliadores-grid">
              {avaliadores.map(a => (
                <div key={a.id} className="premio-avaliador-item">
                  <span className="premio-avaliador-num">{a.numero_identificacao}</span>
                  <div className="premio-avaliador-info">
                    <span className="premio-avaliador-nome">
                      {a.first_name} {a.last_name}
                    </span>
                    <span className="premio-avaliador-user">@{a.username}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {resultados.length === 0 ? (
        <div className="premio-vazio">
          <p>Nenhuma apresentação de {info.titulo} encontrada.</p>
          <small>Crie uma sessão com categoria "{info.emoji} {info.titulo}" no painel Admin.</small>
        </div>
      ) : (
        <div>

          {/* Abas de método de classificação */}
          <div style={{ display:'flex', gap:'8px', marginBottom:'20px', flexWrap:'wrap' }}>
            {[
              { key:'media',   label:'📊 Média Aritmética',  desc:'Método oficial' },
              { key:'aparada', label:'✂️ Média Aparada',     desc:'Remove maior e menor nota' },
              { key:'zscore',  label:'📐 Z-Score',           desc:'Neutraliza viés do avaliador' },
            ].map(aba => (
              <button
                key={aba.key}
                onClick={() => setAbaAtiva(aba.key)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: abaAtiva === aba.key ? '2px solid #c9a84c' : '2px solid #e0e0e0',
                  background: abaAtiva === aba.key ? '#1a2e45' : 'white',
                  color: abaAtiva === aba.key ? '#c9a84c' : '#666',
                  fontWeight: abaAtiva === aba.key ? '700' : '400',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                }}
              >
                {aba.label}
                <span style={{ display:'block', fontSize:'11px', color: abaAtiva === aba.key ? 'rgba(201,168,76,0.7)' : '#aaa', fontWeight:'400' }}>
                  {aba.desc}
                </span>
              </button>
            ))}
          </div>

          {/* Descrição do método ativo */}
          <div style={{ background: abaAtiva === 'zscore' ? '#f0f4ff' : abaAtiva === 'aparada' ? '#f0fff4' : '#fffbf0', border: '1px solid', borderColor: abaAtiva === 'zscore' ? '#c5d0e8' : abaAtiva === 'aparada' ? '#b2dfdb' : '#e8d5a0', borderRadius:'8px', padding:'10px 16px', marginBottom:'20px', fontSize:'13px', color:'#555' }}>
            {abaAtiva === 'media' && <span>📊 <strong>Média Aritmética:</strong> Soma de todas as notas dividida pelo número de avaliadores. Método simples e direto.</span>}
            {abaAtiva === 'aparada' && <span>✂️ <strong>Média Aparada:</strong> Remove a maior e a menor nota antes de calcular a média. Elimina avaliadores muito rigorosos ou muito generosos.</span>}
            {abaAtiva === 'zscore' && <span>📐 <strong>Z-Score:</strong> Normaliza as notas de cada avaliador pela sua média e desvio padrão. Quando ainda não há histórico suficiente, usa a distribuição geral para considerar todas as avaliações realizadas.</span>}
          </div>

          {resultadosOrdenados.map((resultado, index) => {
            const notaExibida = (() => {
              if (resultado.avaliacoes.length === 0) return null;
              if (abaAtiva === 'media')   return calcMedia(resultado.avaliacoes);
              if (abaAtiva === 'aparada') return calcMediaAparada(resultado.avaliacoes);
              if (abaAtiva === 'zscore')  return calcZScore(resultado.id, resultados);
              return calcMedia(resultado.avaliacoes);
            })();
            const premiado = notaExibida !== null && index < PREMIACAO_TOP_5.length;
            const medalha = premiado ? PREMIACAO_TOP_5[index].icone : '';
            const dadosFaltantes = faltantes[resultado.id];
            const estaAberta = resultado.avaliacao_aberta;

            return (
              <div key={resultado.id} className={'premio-ap-card ' + (premiado ? 'premio-ap-card-premiado' : '')}>

                {/* Linha principal da apresentação */}
                <div className="premio-ap-main" onClick={() => toggleDetalhes(resultado.id)}>
                  <div className="premio-ap-pos">
                    {medalha && <span className="premio-medalha" title={`${index + 1}º colocado — premiado`}>{medalha}</span>}
                    <span className="premio-pos-num">{index + 1}º</span>
                    {premiado && (
                      <span className={`premio-premiado-label premio-premiado-label-${PREMIACAO_TOP_5[index].classe}`}>
                        {PREMIACAO_TOP_5[index].etiqueta}
                      </span>
                    )}
                  </div>
                  <div className="premio-ap-info">
                    <span className="premio-ap-titulo">{resultado.titulo}</span>
                    <span className="premio-ap-apresentador">👤 {resultado.apresentador_principal}</span>
                  </div>
                  <div className="premio-ap-stats">
                    <span className="premio-ap-avals">{resultado.avaliacoes.length} aval.</span>
                    {notaExibida === null
                      ? <span className="premio-sem-aval">—</span>
                      : abaAtiva === 'zscore'
                        ? <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'2px'}}>
                            <span className={'premio-nota ' + (notaExibida >= 0 ? 'nota-alta' : 'nota-baixa')}>
                              {notaExibida > 0 ? '+' : ''}{notaExibida.toFixed(3)}
                              <span style={{fontSize:'10px', marginLeft:'2px', opacity:0.7}}>σ</span>
                            </span>
                            <span style={{fontSize:'11px', color:'#888', fontWeight:'500'}}>
                              ≈ {Math.min(100, Math.max(0, 50 + notaExibida * 10)).toFixed(1)} pts
                            </span>
                          </div>
                        : <span className={'premio-nota ' + (notaExibida >= 8 ? 'nota-alta' : notaExibida >= 6 ? 'nota-media' : 'nota-baixa')}>
                            {notaExibida.toFixed(2)}
                          </span>
                    }
                  </div>
                </div>

                {/* Controles de abertura/fechamento — apenas coordenador e superusuário */}
                {podeControlar && (
                  <div className="premio-controle">
                    <span className={'premio-status-badge ' + (estaAberta ? 'status-aberta' : 'status-fechada')}>
                      {estaAberta ? '🟢 Avaliação Aberta' : '🔴 Avaliação Fechada'}
                    </span>
                    <div className="premio-controle-btns">
                      {!estaAberta ? (
                        <button
                          className="premio-btn-abrir"
                          onClick={() => handleControle(resultado, 'abrir')}
                          disabled={controlandoId === resultado.id}
                        >
                          {controlandoId === resultado.id ? '⏳' : '▶ Abrir Avaliação'}
                        </button>
                      ) : (
                        <button
                          className="premio-btn-fechar"
                          onClick={() => handleControle(resultado, 'fechar')}
                          disabled={controlandoId === resultado.id}
                        >
                          {controlandoId === resultado.id ? '⏳' : '⏹ Fechar Avaliação'}
                        </button>
                      )}
                      {!estaAberta && (
                        <button
                          className="premio-btn-faltantes"
                          onClick={() => dadosFaltantes ? setFaltantes(prev => { const n={...prev}; delete n[resultado.id]; return n; }) : buscarFaltantes(resultado.id)}
                          disabled={loadingFaltantes[resultado.id]}
                        >
                          {loadingFaltantes[resultado.id] ? '⏳' : dadosFaltantes ? '✕ Ocultar' : '👥 Ver Faltantes'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Painel de faltantes */}
                {dadosFaltantes && (
                  <div className="premio-faltantes-painel">
                    <div className="premio-faltantes-header">
                      <div>
                        <strong>👥 Controle de Avaliações</strong>
                        <span className="premio-faltantes-resumo">
                          &nbsp;·&nbsp;
                          <span className="faltou-count">{dadosFaltantes.faltantes.length} não avaliaram</span>
                          &nbsp;·&nbsp;
                          <span className="avaliou-count">{dadosFaltantes.ja_avaliaram.length} avaliaram</span>
                          &nbsp;de&nbsp;{dadosFaltantes.total_avaliadores}
                        </span>
                      </div>
                      <button className="premio-btn-exportar-faltantes" onClick={() => exportarFaltantesExcel(resultado)}>
                        📊 Exportar Excel
                      </button>
                    </div>

                    {dadosFaltantes.faltantes.length === 0 ? (
                      <p className="premio-todos-avaliaram">✅ Todos os avaliadores já avaliaram esta apresentação!</p>
                    ) : (
                      <div className="premio-faltantes-lista">
                        <p className="premio-faltantes-titulo">❌ Não avaliaram:</p>
                        <div className="premio-faltantes-tags">
                          {dadosFaltantes.faltantes.map(u => (
                            <span key={u.id} className="premio-faltante-tag">
                              {u.numero_identificacao && (
                                <span style={{
                                  background:'#842029', color:'white',
                                  borderRadius:'50%', width:'18px', height:'18px',
                                  display:'inline-flex', alignItems:'center', justifyContent:'center',
                                  fontSize:'10px', fontWeight:'800', marginRight:'6px', flexShrink:0
                                }}>{u.numero_identificacao}</span>
                              )}
                              👤 {u.nome_completo} <code>@{u.username}</code>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {dadosFaltantes.ja_avaliaram.length > 0 && (
                      <div className="premio-avaliaram-lista">
                        <p className="premio-avaliaram-titulo">✅ Já avaliaram:</p>
                        <div className="premio-faltantes-tags">
                          {dadosFaltantes.ja_avaliaram.map(u => (
                            <span key={u.id} className="premio-avaliou-tag">
                              {u.numero_identificacao && (
                                <span style={{
                                  background:'#0f5132', color:'white',
                                  borderRadius:'50%', width:'18px', height:'18px',
                                  display:'inline-flex', alignItems:'center', justifyContent:'center',
                                  fontSize:'10px', fontWeight:'800', marginRight:'6px', flexShrink:0
                                }}>{u.numero_identificacao}</span>
                              )}
                              ✓ {u.nome_completo}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Detalhes das avaliações */}
                {detalhesVisiveis[resultado.id] && (
                  <div className="premio-detalhes-linha">
                    {detalhesAvaliacoes[resultado.id]
                      ? <AvaliacoesDetalhes
                          avaliacoes={detalhesAvaliacoes[resultado.id]}
                          onUpdate={() => {
                            setDetalhesAvaliacoes(prev => { const n = {...prev}; delete n[resultado.id]; return n; });
                            fetchResultados();
                          }}
                        />
                      : <p style={{padding:'10px', color:'#6b7a95'}}>Carregando detalhes…</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ResultadosPremio;
